/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('DOMContentLoaded', function () {
  if (window.location.pathname !== '/policies/privacy-policy') return;

  var container = document.querySelector('.shopify-policy__container');
  var title = document.querySelector('.shopify-policy__title');
  var body = document.querySelector('.shopify-policy__body');
  if (!container || !title || !body || container.dataset.aomoEnhanced === 'true') return;

  container.dataset.aomoEnhanced = 'true';
  document.body.classList.add('aomo-privacy-page');

  var heading = title.querySelector('h1');
  if (heading) heading.textContent = 'Privacy Policy';
  var heroInner = document.createElement('div');
  heroInner.className = 'aomo-policy-hero__inner';
  var eyebrow = document.createElement('span');
  eyebrow.className = 'aomo-policy-hero__eyebrow';
  eyebrow.textContent = 'AOMOSUR · Data Protection';
  var subtitle = document.createElement('p');
  subtitle.className = 'aomo-policy-hero__subtitle';
  subtitle.textContent = 'How we collect, use and protect information when you shop with AOMOSUR.';
  heroInner.appendChild(eyebrow);
  if (heading) heroInner.appendChild(heading);
  heroInner.appendChild(subtitle);
  title.appendChild(heroInner);

  var layout = document.createElement('div');
  layout.className = 'aomo-policy-layout';
  body.parentNode.insertBefore(layout, body);

  var sidebar = document.createElement('aside');
  sidebar.className = 'aomo-policy-sidebar';
  sidebar.setAttribute('aria-label', 'Privacy policy navigation');
  var sidebarTitle = document.createElement('strong');
  sidebarTitle.className = 'aomo-policy-sidebar__title';
  sidebarTitle.textContent = 'On this page';
  sidebar.appendChild(sidebarTitle);

  var nav = document.createElement('nav');
  body.querySelectorAll('h2').forEach(function (sectionHeading, index) {
    var sectionId = 'privacy-section-' + (index + 1);
    sectionHeading.id = sectionId;
    var link = document.createElement('a');
    link.href = '#' + sectionId;
    link.textContent = sectionHeading.textContent.replace(/^\d+\.\s*/, '');
    nav.appendChild(link);
  });
  sidebar.appendChild(nav);

  var support = document.createElement('div');
  support.className = 'aomo-policy-sidebar__support';
  support.innerHTML = '<strong>Privacy question?</strong><span>Our support team can help.</span><a href="/pages/contact">Contact AOMOSUR →</a>';
  sidebar.appendChild(support);
  layout.appendChild(sidebar);
  layout.appendChild(body);

  var rte = body.querySelector('.rte');
  if (rte) {
    var highlights = document.createElement('div');
    highlights.className = 'aomo-policy-highlights';
    highlights.innerHTML = '<div><strong>Clear use</strong><span>We explain why information is collected.</span></div><div><strong>No data sales</strong><span>We do not sell your personal information.</span></div><div><strong>Your rights</strong><span>Access, correction and deletion requests are supported.</span></div>';
    var policyContent = rte.querySelector(':scope > div') || rte;
    var effectiveDate = policyContent.querySelector('.aomo-policy__effective');
    if (effectiveDate && effectiveDate.nextSibling) {
      policyContent.insertBefore(highlights, effectiveDate.nextSibling);
    } else {
      policyContent.insertBefore(highlights, policyContent.firstChild);
    }
  }
});
